import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook, Youtube, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { MAP_LINK } from "@/data/content";

const sitemap = [
  { to: "/", label: "Home" },
  { to: "/brands", label: "Brands" },
  { to: "/creators", label: "Creators" },
  { to: "/ugc", label: "UGC" },
  { to: "/agency", label: "Agency" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const legal = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms of Service" },
  { to: "/refund", label: "Refund Policy" },
  { to: "/cookies", label: "Cookie Policy" },
  { to: "/disclaimer", label: "Disclaimer" },
];

const socials = [
  { Icon: Facebook, href: "https://www.facebook.com/share/18qbbYgUmb/", label: "facebook" },
  { Icon: Instagram, href: "https://www.instagram.com/incer_official/", label: "instagram" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/incerinfluencermarketingagency/", label: "linkedin" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[#1a1a1a] bg-[#050505] grain-bg"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block">
              <Logo className="h-14 md:h-16 w-auto" />
            </Link>
            <p className="mt-6 max-w-md text-[#A3A3A3] leading-relaxed">
              Strategy-driven influencer marketing platform powering brands through verified creators,
              campaign management, and real-time performance analytics.
            </p>
            <div className="mt-8 flex gap-3">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-social-${label}`}
                  className="w-11 h-11 rounded-full border border-[#222] flex items-center justify-center text-[#A3A3A3] hover:text-[#FF4A00] hover:border-[#FF4A00] transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Sitemap */}
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-5">Sitemap</div>
            <ul className="space-y-3">
              {sitemap.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    data-testid={`footer-link-${s.label.toLowerCase()}`}
                    className="text-[#F3E8E0] hover:text-[#FF4A00] transition-colors inline-flex items-center gap-1.5 group"
                  >
                    {s.label}
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-3">
            <div className="label-eyebrow mb-5">Legal</div>
            <ul className="space-y-3">
              {legal.map((s) => (
                <li key={s.to}>
                  <Link
                    to={s.to}
                    data-testid={`footer-legal-${s.to.replace("/", "")}`}
                    className="text-[#F3E8E0] hover:text-[#FF4A00] transition-colors inline-flex items-center gap-1.5 group"
                  >
                    {s.label}
                    <ArrowUpRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <div className="label-eyebrow mb-5">Get in touch</div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-[#F3E8E0]">
                <Mail className="w-5 h-5 mt-0.5 text-[#FF4A00] shrink-0" />
                <a
                  href="mailto:contact@incer.in"
                  data-testid="footer-email"
                  className="hover:text-[#FF4A00] transition-colors break-all"
                >
                  contact@incer.in
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#F3E8E0]">
                <Phone className="w-5 h-5 mt-0.5 text-[#FF4A00] shrink-0" />
                <a
                  href="tel:+919342937974"
                  data-testid="footer-phone"
                  className="hover:text-[#FF4A00] transition-colors"
                >
                  +91 90872 55512
                </a>
              </li>
              <li className="flex items-start gap-3 text-[#A3A3A3]">
                <MapPin className="w-5 h-5 mt-0.5 text-[#FF4A00] shrink-0" />
                <a
                  href={MAP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-address"
                  className="hover:text-[#FF4A00] transition-colors"
                >
                  45/13 Vettukattuvalasu, Vivekanandar Salai,
                  <br />
                  Erode – 638011, Tamil Nadu, India
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#1a1a1a] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-sm text-[#666]">
            © {new Date().getFullYear()} Incer Technovation Pvt Ltd. All rights reserved.
          </p>
          <p className="text-sm text-[#666]">
            Powering brands through strategic influence.
          </p>
        </div>
      </div>
    </footer>
  );
}
